import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Alert,
  alpha,
  useTheme,
  Skeleton,
  Chip,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  EventBusy,
  CheckCircle,
  HourglassEmpty,
  TrendingUp,
  Block,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useSnackbar } from 'notistack';
import CardTooltip from '../../components/common/CardTooltip';
import leaveService, { MyLeaveBalanceResponse, LeaveAnalyticsResponse } from '../../services/leaveService';

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }: any) => {
  const theme = useTheme();
  if (active && payload && payload.length) {
    return (
      <Paper
        sx={{
          p: 1.5,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {label}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: entry.color }}
          >
            {entry.name}: {entry.value} days
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

const MyLeavesPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [balanceData, setBalanceData] = useState<MyLeaveBalanceResponse | null>(null);
  const [analyticsData, setAnalyticsData] = useState<LeaveAnalyticsResponse | null>(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [balance, analytics] = await Promise.all([
        leaveService.getMyLeaveBalance(),
        leaveService.getMyLeaveAnalytics(),
      ]);
      setBalanceData(balance);
      setAnalyticsData(analytics);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load leave data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate totals
  const totalAllocated = balanceData?.balances?.reduce((sum, b) => sum + b.allocated + b.carryForward, 0) || 0;
  const totalUsed = balanceData?.balances?.reduce((sum, b) => sum + b.used, 0) || 0;
  const totalPending = balanceData?.balances?.reduce((sum, b) => sum + b.pending, 0) || 0;
  const totalAvailable = balanceData?.balances?.reduce((sum, b) => sum + b.available, 0) || 0;

  // Prepare pie chart data
  const pieChartData = analyticsData?.typeBreakdown?.filter(t => t.days > 0).map(t => ({
    name: t.name,
    value: t.days,
    color: t.color,
  })) || [];

  // Calculate totals for tooltip
  const totalAllocatedBase = balanceData?.balances?.reduce((sum, b) => sum + b.allocated, 0) || 0;
  const totalCarryForward = balanceData?.balances?.reduce((sum, b) => sum + b.carryForward, 0) || 0;

  // Get cycle year display from backend response
  const cycleYearDisplay = analyticsData?.cycleYearDisplay || balanceData?.cycleYearDisplay || new Date().getFullYear().toString();

  // Render stat card
  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    suffix = '',
    tooltip
  }: { 
    title: string; 
    value: number | string; 
    icon: React.ReactNode; 
    color: string;
    suffix?: string;
    tooltip?: React.ReactNode;
  }) => {
    const cardContent = (
    <Card
      elevation={0}
      sx={{
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(color, 0.2)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color }}>
              {value}{suffix}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(color, 0.15),
              color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
  
  return tooltip ? (
    <CardTooltip title={tooltip}>
      <Box sx={{ cursor: 'help' }}>
        {cardContent}
      </Box>
    </CardTooltip>
  ) : cardContent;
};

  // Loading skeleton
  if (loading && !balanceData) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Skeleton variant="rounded" height={350} />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Skeleton variant="rounded" height={350} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  // No policy assigned
  if (!balanceData?.policy) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="70vh"
      >
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            maxWidth: 420,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Block 
            sx={{ 
              fontSize: 56, 
              color: 'text.disabled',
              mb: 2 
            }} 
          />
          <Typography variant="h6" fontWeight="600" gutterBottom color="text.primary">
            No Leave Policy Assigned
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 1 }}>
             No leave policy has been assigned to your role yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
             Please contact your administrator to assign a policy.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            My Leaves
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your leave balance and usage analytics
          </Typography>
        </Box>
        
        <Chip 
          label={`Policy: ${balanceData.policy.name} (${cycleYearDisplay})`}
          variant="outlined"
          color="primary"
        />
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Total Allocated"
            value={totalAllocated}
            suffix=" days"
            icon={<TrendingUp />}
            color={theme.palette.primary.main}
            tooltip={
              <Box sx={{ fontSize: '0.85rem' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  📊 Breakdown
                </Typography>
                <Box sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  Base Allocation: <strong>{totalAllocatedBase}</strong>
                  <br />
                  Carry Forward: <strong>{totalCarryForward}</strong>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Total = <strong>{totalAllocated} days</strong>
                </Typography>
              </Box>
            }
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Used"
            value={totalUsed}
            suffix=" days"
            icon={<CheckCircle />}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Pending"
            value={totalPending}
            suffix=" days"
            icon={<HourglassEmpty />}
            color={theme.palette.warning.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Available"
            value={totalAvailable}
            suffix=" days"
            icon={<EventBusy />}
            color={theme.palette.info.main}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Pie Chart - Leave Type Breakdown */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              minHeight: 350,
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Leave Usage by Type
            </Typography>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value) => <span style={{ color: theme.palette.text.primary }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Alert severity="info" sx={{ maxWidth: 300 }}>
                  No leave usage data yet
                </Alert>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Area Chart - Monthly Trend */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              minHeight: 350,
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Monthly Leave Usage ({cycleYearDisplay})
            </Typography>
            {analyticsData?.monthlyTrend && analyticsData.monthlyTrend.some(m => m.days > 0) ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={analyticsData.monthlyTrend}>
                  <defs>
                    <linearGradient id="colorLeaves" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  />
                  <YAxis tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="days"
                    name="Days Used"
                    stroke={theme.palette.primary.main}
                    fillOpacity={1}
                    fill="url(#colorLeaves)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Alert severity="info" sx={{ maxWidth: 300 }}>
                  No monthly usage data available
                </Alert>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Leave Balance Cards */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Leave Balance by Type
      </Typography>
      <Grid container spacing={2}>
        {balanceData.balances.map((balance) => {
          const leaveType = balance.leaveTypeId;
          const total = balance.allocated + balance.carryForward;
          const usedPercentage = total > 0 ? ((balance.used + balance.pending) / total) * 100 : 0;
          
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={balance._id}>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  borderLeft: `4px solid ${leaveType.color}`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 2,
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: leaveType.color,
                        }}
                      />
                      <Typography variant="subtitle1" fontWeight={600}>
                        {leaveType.name}
                      </Typography>
                    </Box>
                    <Chip
                      label={leaveType.isPaid ? 'Paid' : 'Unpaid'}
                      size="small"
                      sx={{
                        bgcolor: alpha(leaveType.isPaid ? theme.palette.success.main : theme.palette.warning.main, 0.1),
                        color: leaveType.isPaid ? theme.palette.success.main : theme.palette.warning.main,
                        fontWeight: 500,
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(usedPercentage, 100)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(leaveType.color, 0.1),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: leaveType.color,
                          borderRadius: 4,
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                      <Typography variant="h6" fontWeight={700} color="primary">
                        {balance.available}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Available
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                      <Typography variant="h6" fontWeight={600} color="text.secondary">
                        {balance.used}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Used
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                      <Typography variant="h6" fontWeight={600} color="warning.main">
                        {balance.pending}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                      <Typography variant="h6" fontWeight={600} color="text.secondary">
                        {total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total
                      </Typography>
                    </Box>
                  </Box>

                  {balance.carryForward > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Includes {balance.carryForward} days carried forward
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default MyLeavesPage;
